import { addClass, toggleClass, closest, hasClass, parents, removeClass } from '../util/dom';
import anime from 'animejs';
import { addClickEventListener, addDocumentTargetClickEventListener } from '../util/event';
import { refreshWaypoints } from "../scroll/exposeSubNav";

const CONTAINER_SELECTOR = '[data-expand="container"]';
const CONTENT_SELECTOR = '[data-expand="content"]';
const GROUP_SELECTOR = '[data-expand="group"]';
const PREVENT_SELECTOR = '[data-expand="prevent"]';
const IS_EXPANDED_CLASS = 'is-expanded';
const EXPAND_LINK_LESS_SELECTOR = '.c-expand__less';
const EXPAND_LINK_MORE_SELECTOR = '.c-expand__more';
const EXPAND_CONTAINER = '[data-expand-cropped="container"]';
const EXPAND_CONTENT = '[data-expand-cropped="content"]';
const CROPPED_CONTAINER = '.c-cropped-container';
const IS_EXPANDED = 'is-expanded';

const EXPAND_ITEM_COUNT = 8;
const DESK_WIDTH = 1025;

export enum TriggerType {
    MORE = 'more',
    LESS = 'less',
}

export class Expander {
    public static init(): void {
        addDocumentTargetClickEventListener('[data-expand="trigger"]', event => {
            event.preventDefault();
            if (event.target === null) {
                return;
            }

            const element = <HTMLElement>(<HTMLElement>event.target).closest('[data-expand="trigger"]');
            if (element === null) {
                return;
            }
            const container = closest(element, CONTAINER_SELECTOR);

            if (
                container !== null
                && hasClass(container, 'is-responsive')
                && document.documentElement.clientWidth >= DESK_WIDTH
            ) {
                return;
            }

            if (element.dataset.expand !== 'prevent' && !parents(element, PREVENT_SELECTOR).length) {
                if (this.isExpanded(element)) {
                    this.close(element);
                } else {
                    this.closeAll(element);
                    this.open(element);
                }
            }
            refreshWaypoints()
        });

        document.querySelectorAll<HTMLElement>('[data-expand="toggle-all"]').forEach((element: HTMLElement) =>
            addClickEventListener(element, event => {
                event.preventDefault();
                const isExpanded = hasClass(element, IS_EXPANDED_CLASS);
                const hasToggleText = ('expandTextCollapsed' in element.dataset && 'expandTextExpanded' in element.dataset);
                const { expandId } = element.dataset;

                const triggers = Array.from(document.querySelectorAll<HTMLElement>(`[data-expand="container"][data-expand-id="${expandId}"]`))
                    .reduce((acc, x) =>
                        acc.concat(Array.from(x.querySelectorAll<HTMLElement>('[data-expand="trigger"]'))),
                        [] as HTMLElement[]
                    );

                const { expandTextCollapsed = '', expandTextExpanded = '' } = element.dataset;

                if (isExpanded) {
                    triggers.forEach(t => this.close(t));
                    removeClass(element, IS_EXPANDED_CLASS);
                    if (hasToggleText) {
                        element.textContent = expandTextCollapsed;
                    }
                } else {
                    triggers.forEach(t => this.open(t));
                    addClass(element, IS_EXPANDED_CLASS);

                    if (hasToggleText) {
                        element.textContent = expandTextExpanded;
                    }
                }
            })
        );

        document.querySelectorAll<HTMLElement>('[data-expand="trigger-highlights"]').forEach((element: HTMLElement) =>
            addClickEventListener(element, event => {
                event.preventDefault();

                if (element.dataset.expand !== 'prevent' && !parents(element, PREVENT_SELECTOR).length) {
                    const container = this.getContainer(element);
                    if (container === null) {
                        return;
                    }
                    const content = this.getContentByContainer(container);
                    if (content === null) {
                        return;
                    }
                    const items = Array.from(content.querySelectorAll<HTMLElement>('.o-layout__item'));
                    const areAllItemsVisible = this.getItemVisibleCount(items) === items.length;

                    if (areAllItemsVisible) {
                        this.beforeClose(items, element, () => this.close(element));
                    } else {
                        this.beforeOpen(items, element, () => this.open(element));
                    }
                }
            }));

        document.querySelectorAll<HTMLElement>('[data-expand="close"]').forEach((element: HTMLElement) =>
            addClickEventListener(element, event => {
                event.preventDefault();
                this.close(element);
            }));
    }

    private static open(element: HTMLElement): void {
        const container = this.getContainer(element);
        const content = this.getContentByContainer(container);

        anime.remove(content);
        addClass(container, IS_EXPANDED_CLASS);
    }

    private static isExpanded(element: HTMLElement): boolean {
        return hasClass(this.getContainer(element), IS_EXPANDED_CLASS);
    }

    private static close(element: HTMLElement): void {
        const container = this.getContainer(element);

        removeClass(container, IS_EXPANDED_CLASS);
    }

    private static getContentByContainer(container: HTMLElement): HTMLElement | null {
        return container.querySelector<HTMLElement>(CONTENT_SELECTOR);
    }

    private static closeAll(element: HTMLElement): void {
        const groupWrapper = element.closest(GROUP_SELECTOR);
        if (groupWrapper !== null) {
            groupWrapper.querySelectorAll<HTMLElement>(CONTAINER_SELECTOR).forEach((e: HTMLElement) => removeClass(e, IS_EXPANDED_CLASS));
        }
    }

    private static getContainer(element: HTMLElement): HTMLElement {
        return <HTMLElement>element.closest(CONTAINER_SELECTOR);
    }

    private static getItemVisibleCount(items: HTMLElement[]): number {
        return items.length - items.filter((e: HTMLElement) => e.matches('.hidden')).length;
    }

    private static getTriggerLabel(trigger: HTMLElement, type: TriggerType): HTMLElement | null {
        return type === TriggerType.LESS
            ? trigger.querySelector<HTMLElement>(EXPAND_LINK_LESS_SELECTOR)
            : trigger.querySelector<HTMLElement>(EXPAND_LINK_MORE_SELECTOR);
    }

    private static getLabels(trigger: HTMLElement): [HTMLElement | null, HTMLElement | null] {
        return [
            this.getTriggerLabel(trigger, TriggerType.LESS),
            this.getTriggerLabel(trigger, TriggerType.MORE)
        ]
    }

    private static beforeOpen(items: HTMLElement[], trigger: HTMLElement, callback?: () => void): void {
        const [lessLabel, moreLabel] = this.getLabels(trigger);

        const itemVisibleCount = this.getItemVisibleCount(items);
        let newLength = itemVisibleCount + EXPAND_ITEM_COUNT;

        if (newLength > items.length) {
            newLength = items.length;
        }

        for (let i = itemVisibleCount; i < newLength; i++) {
            removeClass(items[i], 'hidden');
        }

        if (this.getItemVisibleCount(items) === items.length) {
            if (lessLabel) {
                removeClass(lessLabel, 'hidden');
            }

            if (moreLabel) {
                addClass(moreLabel, 'hidden');
            }
        } else {
            if (moreLabel) {
                removeClass(moreLabel, 'hidden');
            }

            if (lessLabel) {
                addClass(lessLabel, 'hidden');
            }
        }

        if (callback) {
            callback();
        }
    }

    private static beforeClose(items: HTMLElement[], trigger: HTMLElement, callback?: () => void): void {
        const [lessLabel, moreLabel] = this.getLabels(trigger);

        items.forEach(e => addClass(e, 'hidden'));

        if (moreLabel) {
            removeClass(moreLabel, 'hidden');
        }
        if (lessLabel) {
            addClass(lessLabel, 'hidden');
        }

        if (callback) {
            callback();
        }
    }
}

export class ExpandToggle {
    public static init(): void {
        this.cropExpand();

        addDocumentTargetClickEventListener('[data-expand-cropped="trigger"]', event => {
            event.preventDefault();

            const element = <HTMLElement>(<HTMLElement>event.target).closest('[data-expand-cropped="trigger"]');
            if (element !== null) {
                this.toggle(element);
            }
        });
    }

    private static cropExpand(): void {
        const croppedElements = document.querySelectorAll(EXPAND_CONTAINER);

        croppedElements.forEach(croppedElement => {
            const croppedContainer = croppedElement.querySelector<HTMLElement>(CROPPED_CONTAINER);
            const croppedText = croppedElement.querySelector<HTMLElement>(EXPAND_CONTENT);

            if (croppedText !== null && croppedContainer !== null && croppedText.scrollHeight > croppedContainer.offsetHeight) {
                const expandLink = croppedElement.querySelector('.js-toggle-cropped-text');

                if (expandLink !== null) {
                    expandLink.classList.remove('is-hidden');
                }
            }
        })
    }

    private static toggle(element: HTMLElement): void {
        const container = this.getContainer(element);
        const content = this.getContentByContainer(container);

        if (content !== null) {
            toggleClass(container, IS_EXPANDED);
            toggleClass(content, IS_EXPANDED);
        }
    }

    private static getContentByContainer(container: HTMLElement): HTMLElement | null {
        return container.querySelector<HTMLElement>(EXPAND_CONTENT);
    }

    private static getContainer(element: HTMLElement): HTMLElement {
        return <HTMLElement>element.closest(EXPAND_CONTAINER);
    }
}
